import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from '../i18n';
import { get } from './api';

export interface PublicSettings {
  company: { name: string; phone: string; email: string; address: string; timezone: string; currency: string; defaultLanguage: 'fr' | 'en' };
  cutoff: { mode: 'HOURS' | 'BUSINESS_DAY'; hours: number; businessDays: number };
  planning: { openingTime: string; closingTime: string; workingDays: number[] };
  extras: { code: string; labelFr: string; labelEn: string }[];
  serviceAreas: { name: string; sectors: string[] }[];
}

/** Public business settings (timezone, currency, cutoff). Cached for the session. */
export function usePublicSettings() {
  return useQuery({ queryKey: ['public-settings'], queryFn: () => get<PublicSettings>('/public/settings'), staleTime: 10 * 60_000 });
}

/**
 * Formatting in the BUSINESS timezone (America/Toronto by default), never the
 * browser's: an employee travelling or a server in UTC must see the same times.
 */
export function useFormat() {
  const { i18n } = useTranslation();
  const { data } = usePublicSettings();
  const tz = data?.company.timezone ?? 'America/Toronto';
  const currency = data?.company.currency ?? 'CAD';
  const locale = i18n.language === 'en' ? 'en-CA' : 'fr-CA';

  const fmt = useMemo(() => {
    const dtf = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { timeZone: tz, ...o });
    return {
      day: dtf({ weekday: 'long', day: 'numeric', month: 'long' }),
      dayShort: dtf({ weekday: 'short', day: 'numeric', month: 'short' }),
      date: dtf({ day: 'numeric', month: 'long', year: 'numeric' }),
      dateShort: dtf({ day: 'numeric', month: 'short' }),
      time: dtf({ hour: '2-digit', minute: '2-digit', hour12: false }),
      dateTime: dtf({ day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }),
      money: new Intl.NumberFormat(locale, { style: 'currency', currency }),
      ymd: new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }),
      hm: new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }),
    };
  }, [locale, tz, currency]);

  const toDate = (v: string | Date) => (typeof v === 'string' ? new Date(v) : v);
  const fromLocalDate = (d: string) => new Date(`${d}T12:00:00Z`);

  return {
    tz,
    locale,
    /** A local wall-clock time ("09:00" or "09:00:00") in the interface language: "9 h 00" or "9:00". */
    clock: (hhmm: string | null | undefined) => {
      if (!hhmm) return '';
      const [h = '0', m = '00'] = hhmm.slice(0, 5).split(':');
      return i18n.language === 'en' ? `${Number(h)}:${m}` : `${Number(h)} h ${m}`;
    },
    money: useCallback((cents: number | null | undefined) => (cents == null ? '—' : fmt.money.format(cents / 100)), [fmt]),
    day: (v: string | Date) => capitalize(fmt.day.format(toDate(v))),
    dayShort: (v: string | Date) => fmt.dayShort.format(toDate(v)),
    date: (v: string | Date) => fmt.date.format(toDate(v)),
    dateShort: (v: string | Date) => fmt.dateShort.format(toDate(v)),
    time: (v: string | Date) => fmt.time.format(toDate(v)),
    dateTime: (v: string | Date) => fmt.dateTime.format(toDate(v)),
    /** Formats a 'YYYY-MM-DD' calendar date (no timezone shift). */
    localDate: (d: string, style: 'long' | 'short' = 'long') =>
      new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...(style === 'long' ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short' }) }).format(fromLocalDate(d)),
    /** 'YYYY-MM-DD' of an instant in the business timezone. */
    ymd: (v: string | Date) => fmt.ymd.format(toDate(v)),
    /** 'HH:mm' of an instant in the business timezone. */
    hm: (v: string | Date) => fmt.hm.format(toDate(v)),
    duration: (minutes: number) => formatDuration(minutes, i18n.language === 'fr' ? 'fr' : 'en'),
    todayYmd: () => fmt.ymd.format(new Date()),
  };
}

/** "2 h 35" in French, "2h 35m" in English (defaults to the interface language). */
export function formatDuration(minutes: number, lang: 'fr' | 'en' = i18next.language === 'fr' ? 'fr' : 'en') {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  if (lang === 'en') return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function addDays(ymd: string, n: number) {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function weekdayOf(ymd: string) {
  return new Date(`${ymd}T00:00:00Z`).getUTCDay();
}

export function startOfWeek(ymd: string) {
  const wd = weekdayOf(ymd);
  return addDays(ymd, wd === 0 ? -6 : 1 - wd);
}

export function googleMapsUrl(p: { lat?: number | null; lng?: number | null; address?: string | null; city?: string | null }) {
  const dest = p.lat != null && p.lng != null ? `${p.lat},${p.lng}` : encodeURIComponent(`${p.address ?? ''}, ${p.city ?? ''}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

/** Smooth scrolling only when the user has not asked for reduced motion. */
export function scrollBehavior(): ScrollBehavior {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}
